import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { addPaywallSchema } from 'utils/paywallUtils';
import { formatAmpersand } from 'utils/miscUtils/myFavorites';
import { PageType } from 'interfaces/content/articles/Post';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import { addStnMetaSchema } from 'utils/metaDataUtils/setJsonLdSchema';
import { UserContext } from 'providers/UserProvider';
import { commonColors } from 'styles/colors';
import { objectToLiteralString } from 'utils/stringUtils';
import type CustomWindow from 'interfaces/Utils';
import customOneTrustLocation from 'utils/miscUtils/customOneTrustLocation';
import Cookies from 'js-cookie';
import type { SubscriberContext } from 'services/Subscriber';
import { isUserSubscribed } from '@/app_lib/utils/clientUtils';
export interface HeadProps {
  authors?: string;
  databaseId: string;
  dateGmt: string | null;
  description: string;
  faqSchema: string;
  hasStnVideo: boolean;
  hasVanityUrl?: boolean;
  isBranded?: boolean;
  image: string;
  imageTwitter?: string;
  jsonLdSchema: string;
  keywords: string;
  link: string;
  pageAdConfig?: PageAdConfig;
  pianoPageConfig: PianoPageConfig;
  premiumCategory: string;
  primarySection: string;
  socialDescription: string;
  socialTitle: string;
  tagNames: string[];
  title: string;
  pageType: PageType;
  canonicalUrl: string | null;
  redVenture?: boolean;
  siteName?: string;
  subDomain?: string;
  useBinanceWidget?: boolean;
  rssFeedLink: string | null;
  widgetCss?: string | null;
  prefixedDatabaseId?: string | null;
  noIndex?: boolean | null;
  templateType?: string;
}
interface OneTrustUser {
  dataSubjectParams: {
    isAnonymous: boolean; // Tells OneTrust whether the user is logged in or not; this is handled automatically
    id?: string;
    token?: string;
  };
  geolocationResponse?: {
    stateCode: string;
    countryCode: string;
    regionCode?: string;
  };
}

/** Subscribe URLs where the Freestar Recovered Script is disabled. */
const CONVERSION_LANDING_PAGES = ['/subscribe/', '/email-subscribe/', '/paid-subscribe/'];

declare let window: CustomWindow;
const Head = (props: HeadProps) => {
  const router = useRouter();
  const referrer = useRef<string>('');
  const pageTitle = useRef<string>('');
  const queryParamsRef = useRef<string>('');
  const {
    userState: user,
    hasConsent,
    isMobileApp,
    personalizedAds,
    performanceTracking,
    setHasConsent,
    setPersonalizedAds,
    setPerformanceTracking,
  } = useContext(UserContext);
  const [loadOneTrust, setLoadOneTrust] = useState(false);
  const [loadFreestarRecovered, setLoadFreestarRecovered] = useState(false);
  // Start with default case for non-logged-in user
  const [oneTrustUserObj, setOneTrustUserObj] = useState<OneTrustUser>({
    dataSubjectParams: {
      isAnonymous: true,
    },
  });

  const {
    authors,
    pageType,
    description,
    faqSchema,
    hasVanityUrl,
    isBranded,
    image,
    imageTwitter,
    jsonLdSchema,
    keywords,
    widgetCss,
    link,
    noIndex,
    pageAdConfig,
    pianoPageConfig,
    socialDescription,
    socialTitle,
    title,
    canonicalUrl,
    redVenture,
    siteName = 'Fortune',
    // useBinanceWidget,
    rssFeedLink = '',
  } = props;

  let initialCanonical;

  if (canonicalUrl && canonicalUrl !== '') {
    initialCanonical = canonicalUrl;
  } else {
    initialCanonical = link;
  }
  const [canonical, setCanonical] = useState(initialCanonical);

  // References to piano user handler and OneTrust mount state
  // Used for a watchdog timer that mounts OneTrust if piano doesn't load in time
  // This ensures consent management is mounted if piano gets stuck
  // (check fetchToken call below)
  const userContextRef = useRef<SubscriberContext>(user);
  userContextRef.current = user;
  const hasInitializedOneTrust = useRef<boolean>(loadOneTrust);
  hasInitializedOneTrust.current = loadOneTrust;

  useEffect(() => {
    if (hasVanityUrl) {
      setCanonical(window.location.href);
    }
  }, [hasVanityUrl]);

  useEffect(() => {
    queryParamsRef.current = window.location.search;
    referrer.current = document.referrer;
    pageTitle.current = document.title;
  }, []);

  useEffect(() => {
    const fetchToken = async () => {
      if (user.isLoggedIn && user.pianoUid) {
        const response = await fetch(`${process.env.SITE_BASE_URL}/api/getJwtToken/${user.pianoUid}/`);
        if (!response.ok) {
          throw new Error('Could not get piano user JWT');
        }
        const userToken = (await response.json()) as string;
        setOneTrustUserObj({
          dataSubjectParams: {
            isAnonymous: false,
            id: user.pianoUid,
            token: userToken,
          },
        });
        setLoadOneTrust(true);
      } else if (process.env.ENVIRONMENT === 'qa' && router.query.customGeolocation) {
        Cookies.set('customGeolocation', router.query.customGeolocation as string);
        const geolocationByQuery = customOneTrustLocation(router.query.customGeolocation as string);
        setOneTrustUserObj({
          dataSubjectParams: {
            isAnonymous: true,
          },
          geolocationResponse: geolocationByQuery,
        });
      } else if (process.env.ENVIRONMENT === 'qa' && Cookies.get('customGeolocation')) {
        const geolocationByCookie = customOneTrustLocation(Cookies.get('customGeolocation') as string);
        setOneTrustUserObj({
          dataSubjectParams: {
            isAnonymous: true,
          },
          geolocationResponse: geolocationByCookie,
        });
      }
    };

    if (user.isLoggedIn !== null) {
      // Attempt to load user data if the user service has loaded
      fetchToken()
        .catch((error) => {
          console.error('Error during fetchToken execution, setting user object as anonymous', error);
        })
        // We only require OneTrust to *wait* for the user object in order to sync
        // Load OneTrust once fetching ends, regardless of outcome
        .finally(() => {
          setLoadOneTrust(true);
        });
    } else {
      // Otherwise, set a watchdog timer
      // When the timer expires, if the user service is not functioning as expected
      // (null value on login flag), mount OneTrust without a user object
      // This ensures we load OneTrust regardless of piano working or not
      // This mechanism is required as in normal circumstances, we need to wait for piano to get
      // the user ID so that OneTrust can fetch their preferences
      // However, if piano gets stuck, we don't want to run our website without consent management
      // (which would incur no tracking, no ads etc)
      setTimeout(() => {
        // This side-effect might've already ran again and mounted OneTrust in the meantime
        // Ensure we only run the code if OneTrust has not, in fact, been set to load yet
        if (!hasInitializedOneTrust.current) {
          // If the user context still shows a null value on its login flag, mount OneTrust directly
          if (userContextRef.current.isLoggedIn === null) {
            console.error(
              'Could not load user identity provider in time, mounting consent provider with anonymous user object',
            );
            setLoadOneTrust(true);
          }
        }
      }, 5000);
    }
  }, [user.isLoggedIn, user.pianoUid]);

  useEffect(() => {
    // Default subscriber status values are null
    if (user.isLoggedIn === null || user.hasAccess === null || user.isPaused === null) {
      return;
    }

    setLoadFreestarRecovered(!isUserSubscribed(user.isLoggedIn, user.hasAccess, user.isPaused));
  }, [user.isLoggedIn, user.hasAccess, user.isPaused]);

  useEffect(() => {
    if (loadOneTrust) {
      window.OptanonWrapper = () => {
        const requiredFlags = ['C0001'];
        const performanceFlags = ['C0002', 'C0003'];
        const personalizationFlags = ['C0004', 'C0005'];
        const activeGroups = window.OnetrustActiveGroups?.split(',') || [];
        const gpcSignal =
          typeof navigator !== 'undefined' &&
          (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

        // Determine if the user is in a region that legally requires strict consent enforcement
        const cookieVal = document.cookie.match(/OptanonConsent=([^;]+)/)?.[1] || '';
        const geoMatch = decodeURIComponent(cookieVal).match(/geolocation=([^&]+)/);
        const [geoCountry, geoState] = geoMatch ? geoMatch[1].split(';') : ['', ''];
        const isStrictRegion = geoState === 'CA' || geoCountry !== 'US';

        if (activeGroups.length > 0) {
          const hasRequiredFlags = requiredFlags.every((flag) => activeGroups.includes(flag));
          const consentBasedAds = personalizationFlags.every((flag) => activeGroups.includes(flag));
          let hasPersonalizedAdsFlags = consentBasedAds;
          if (gpcSignal) {
            hasPersonalizedAdsFlags = false;
          } else if (!isStrictRegion) {
            hasPersonalizedAdsFlags = true;
          }
          const hasPerformanceFlags = performanceFlags.every((flag) => activeGroups.includes(flag));
          setHasConsent(hasRequiredFlags);
          setPerformanceTracking(hasPerformanceFlags);
          setPersonalizedAds(hasPersonalizedAdsFlags);

          if (typeof window.gtag === 'function') {
            window.gtag('consent', 'update', {
              ad_storage: hasPersonalizedAdsFlags ? 'granted' : 'denied',
              ad_user_data: hasPersonalizedAdsFlags ? 'granted' : 'denied',
              ad_personalization: hasPersonalizedAdsFlags ? 'granted' : 'denied',
              analytics_storage: hasPerformanceFlags ? 'granted' : 'denied',
              functionality_storage: activeGroups.includes('C0003') ? 'granted' : 'denied',
              personalization_storage: hasPersonalizedAdsFlags ? 'granted' : 'denied',
            });
          }
        }
      };
      window.OptanonWrapper();
    }
  }, [loadOneTrust]);

  let jsonLdSchemaFinal;
  if (
    pianoPageConfig &&
    [
      PageType.ARTICLE,
      PageType.COMPANY,
      PageType.LONGFORM,
      PageType.FRANCHISE_LIST,
      PageType.FRANCHISE_LIST_ITEM,
      PageType.FRANCHISE_LIST_ITEM_COMPANY,
      PageType.FRANCHISE_LIST_SEARCH,
    ].includes(pianoPageConfig.pageType)
  ) {
    jsonLdSchemaFinal = addPaywallSchema(jsonLdSchema, pianoPageConfig);
  } else {
    jsonLdSchemaFinal = jsonLdSchema;
  }
  const parsedSchema = jsonLdSchemaFinal ? JSON.parse(jsonLdSchemaFinal) : [];
  // Only add STN Meta if the schema exists and has stnMeta property on any of its nodes
  const hasStnMeta = !Array.isArray(parsedSchema)
    ? parsedSchema.stnMeta
    : parsedSchema.some((schema) => schema.stnMeta);
  if (hasStnMeta && pageAdConfig) {
    jsonLdSchemaFinal = addStnMetaSchema(jsonLdSchemaFinal, pageAdConfig);
  }

  function getRobotsContent() {
    const robotsMetaContent: string[] = [];

    if ([PageType.EVERGREEN, PageType.ARTICLE].includes(pageType)) {
      robotsMetaContent.push('max-image-preview:large');
    }

    return robotsMetaContent.toString().replaceAll(',', ', ');
  }

  function setUserObjScriptVal(obj: OneTrustUser | null | undefined) {
    if (obj) {
      return objectToLiteralString(obj);
    }
    return "''";
  }

  const dataDomainScriptId = process.env.ONE_TRUST_DOMAIN_ID;
  const onetrustAutoBlockEnabled = process.env.ONE_TRUST_AUTO_BLOCK;
  const oneTrustScript = onetrustAutoBlockEnabled
    ? `https://cdn.cookielaw.org/consent/${dataDomainScriptId}/OtAutoBlock.js`
    : `https://cdn.cookielaw.org/consent/${dataDomainScriptId}/otSDKStub.js`;
  const shouldHaveAuthorMeta = authors && [PageType.ARTICLE, PageType.EVERGREEN, PageType.LONGFORM].includes(pageType);

  // For Piano ARI: use og:type "article" only for article-like content; "website" for all other pages (homepage, section, subscription, etc.)
  const isArticleOgType = [
    PageType.ARTICLE,
    PageType.ARTICLE_V2,
    PageType.LONGFORM,
    PageType.EVERGREEN,
    PageType.BRANDED_ARTICLE,
  ].includes(pageType);
  const ogTypeContent = isArticleOgType ? 'article' : 'website';

  const asPath = router.asPath ?? router.route ?? '';
  const pathname = router.pathname ?? router.route ?? '';
  const onConversionLandingPage = CONVERSION_LANDING_PAGES.some((page) => asPath.includes(page));
  const skipFreestarRecovered =
    onConversionLandingPage || pathname.includes('/my-account/') || pathname.includes('/newsletters');

  const mixPanelSrc = `
  (function(e,c){if(!c.__SV){var l,h;window.mixpanel=c;c._i=[];c.init=function(q,r,f){function t(d,a){var g=a.split(".");2==g.length&&(d=d[g[0]],a=g[1]);d[a]=function(){d.push([a].concat(Array.prototype.slice.call(arguments,0)))}}var b=c;"undefined"!==typeof f?b=c[f]=[]:f="mixpanel";b.people=b.people||[];b.toString=function(d){var a="mixpanel";"mixpanel"!==f&&(a+="."+f);d||(a+=" (stub)");return a};b.people.toString=function(){return b.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders start_session_recording stop_session_recording people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
  for(h=0;h<l.length;h++)t(b,l[h]);var n="set set_once union unset remove delete".split(" ");b.get_group=function(){function d(p){a[p]=function(){b.push([g,[p].concat(Array.prototype.slice.call(arguments,0))])}}for(var a={},g=["get_group"].concat(Array.prototype.slice.call(arguments,0)),m=0;m<n.length;m++)d(n[m]);return a};c._i.push([q,r,f])};c.__SV=1.2;var k=e.createElement("script");k.type="text/javascript";k.async=!0;k.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?MIXPANEL_CUSTOM_LIB_URL:"file:"===e.location.protocol&&"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)?"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js":"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";e=e.getElementsByTagName("script")[0];e.parentNode.insertBefore(k,e)}})(document,window.mixpanel||[]);
   mixpanel.init('0d4e76fa44d1fcade0554765289b6896', {
      autocapture: true,
      record_sessions_percent: 100,
    });`;
  function searchQueryCheck() {
    const searchQueryRoutes = [
      '/ranking/[franchiseSlug]/search',
      '/ranking-v2/[franchiseSlug]',
      '/ranking/[franchiseSlug]',
    ];

    if (searchQueryRoutes.includes(router.route) && Object.keys(router.query).length > 1) {
      return true;
    }
    return false;
  }

  return (
    <NextHead>
      <title>{formatAmpersand(title)}</title>
      <meta
        name='msapplication-TileColor'
        content='#ffc40d'
      />
      <meta
        name='theme-color'
        content={commonColors.surfaceWhite}
      />
      <meta
        name='description'
        content={description}
      />
      {shouldHaveAuthorMeta && (
        <meta
          name='author'
          content={authors}
        />
      )}
      <meta
        name='keywords'
        content={keywords}
      />
      <meta
        property='og:url'
        content={link}
      />
      <meta
        property='og:type'
        content={ogTypeContent}
      />
      <meta
        property='og:title'
        content={socialTitle}
      />
      <meta
        property='og:description'
        content={socialDescription}
      />
      <meta
        property='og:site_name'
        content={siteName}
      />
      <meta
        property='og:image'
        content={image}
      />
      <meta
        property='og:width'
        content='1024'
      />
      <meta
        property='og:height'
        content='683'
      />
      <meta
        name='twitter:card'
        content='summary_large_image'
      />
      <meta
        name='twitter:title'
        content={socialTitle}
      />
      <meta
        name='twitter:description'
        content={socialDescription}
      />
      <meta
        name='twitter:image'
        content={imageTwitter || image}
      />
      <meta
        name='twitter:url'
        content={link}
      />
      <meta
        property='fb:pages'
        content='56501027948'
      />
      <meta
        name='robots'
        content={getRobotsContent()}
      />
      {router.route === '/advanced-search' && (
        <meta
          name='robots'
          content='noindex, nofollow'
        />
      )}
      {noIndex === true && (
        <meta
          name='robots'
          content='noindex'
        />
      )}
      {searchQueryCheck() && (
        <meta
          name='robots'
          content='noindex'
        />
      )}
      {widgetCss && (
        <style type='text/css'>
          {`
            ${widgetCss}
          `}
        </style>
      )}
      {/* <link
        rel='preconnect'
        href='https://www.googletagmanager.com'
        crossOrigin=''
      /> */}
      <link
        rel='preconnect'
        href='https://www.google-analytics.com'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://www.googletagservices.com'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://ad.doubleclick.net'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://a.pub.network/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://b.pub.network/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://c.pub.network/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://d.pub.network/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://c.amazon-adsystem.com'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://s.amazon-adsystem.com'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://btloader.com/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://api.btloader.com/'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://cdn.confiant-integrations.net'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://static.doubleclick.net'
        crossOrigin=''
      />
      <link
        rel='preconnect'
        href='https://securepubads.g.doubleclick.net'
        crossOrigin=''
      />
      <link
        rel='icon'
        href='/icons/favicons/favicon.ico'
      />
      <link
        rel='apple-touch-icon'
        type='image/png'
        href='/icons/favicons/apple-touch-icon.png'
        sizes='193x193'
      />
      <link
        rel='canonical'
        href={canonical}
      />
      {redVenture && (
        <link
          rel='preconnect'
          href='https://static.myfinance.com'
          crossOrigin=''
        />
      )}
      {/* Temporarily removing these components (BINANCE20260213) */}
      {/* {useBinanceWidget && ( */}
      {/*   <link */}
      {/*     rel='preconnect' */}
      {/*     href='https://public.bnbstatic.com/' */}
      {/*     crossOrigin='' */}
      {/*   /> */}
      {/* )} */}
      {rssFeedLink && (
        <link
          rel='alternate'
          type='application/rss+xml'
          title='Fortune RSS'
          href={rssFeedLink}
        />
      )}
      {faqSchema && (
        <script
          id='faq-json-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: faqSchema }}
        />
      )}
      {jsonLdSchemaFinal && (
        <script
          id='json-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: jsonLdSchemaFinal }}
        />
      )}

      {loadOneTrust && (
        <>
          <script
            id='OneTrustUserObject'
            type='text/javascript'
            data-cy='one-trust-user'
          >
            var OneTrust = {setUserObjScriptVal(oneTrustUserObj)}
          </script>
        </>
      )}
      <script
        async
        src={oneTrustScript}
        data-document-language='true'
        data-domain-script={onetrustAutoBlockEnabled ? '' : dataDomainScriptId}
        type='text/javascript'
        data-cy='one-trust-script'
      />
      {onetrustAutoBlockEnabled && (
        <script
          async
          src={`https://cdn.cookielaw.org/consent/${dataDomainScriptId}/otSDKStub.js`}
          data-document-language='true'
          type='text/javascript'
          data-domain-script={dataDomainScriptId}
        />
      )}
      <script
        ccpa-opt-out-geo='us'
        ccpa-opt-out-ids='C0004'
        ccpa-opt-out-lspa='false'
        src='https://cdn.cookielaw.org/opt-out/otCCPAiab.js'
        type='text/javascript'
      />
      {/* When creating an inline script with template literals, replace '$' with '\$' and '`' with '\`' to prevent unexpected behavior */}
      {!isMobileApp && loadFreestarRecovered && hasConsent && !skipFreestarRecovered && (
        <script
          data-cfasync='false'
          id='freestar-recovered-script'
          type='text/javascript'
        >
          {`
            (()=>{var h=(o,t,i)=>new Promise((e,n)=>{var s=u=>{try{l(i.next(u))}catch(v){n(v)}},d=u=>{try{l(i.throw(u))}catch(v){n(v)}},l=u=>u.done?e(u.value):Promise.resolve(u.value).then(s,d);l((i=i.apply(o,t)).next())});var D,kt=new Uint8Array(16);function G(){if(!D&&(D=typeof crypto!="undefined"&&crypto.getRandomValues&&crypto.getRandomValues.bind(crypto),!D))throw new Error("crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported");return D(kt)}var c=[];for(let o=0;o<256;++o)c.push((o+256).toString(16).slice(1));function st(o,t=0){return c[o[t+0]]+c[o[t+1]]+c[o[t+2]]+c[o[t+3]]+"-"+c[o[t+4]]+c[o[t+5]]+"-"+c[o[t+6]]+c[o[t+7]]+"-"+c[o[t+8]]+c[o[t+9]]+"-"+c[o[t+10]]+c[o[t+11]]+c[o[t+12]]+c[o[t+13]]+c[o[t+14]]+c[o[t+15]]}var Et=typeof crypto!="undefined"&&crypto.randomUUID&&crypto.randomUUID.bind(crypto),W={randomUUID:Et};function Tt(o,t,i){if(W.randomUUID&&!t&&!o)return W.randomUUID();o=o||{};let e=o.random||(o.rng||G)();if(e[6]=e[6]&15|64,e[8]=e[8]&63|128,t){i=i||0;for(let n=0;n<16;++n)t[i+n]=e[n];return t}return st(e)}var B=Tt;typeof document!="undefined"&&(A=document.createElement("style"),A.setAttribute("type","text/css"),A.appendChild(document.createTextNode('div._1bm7ugs{position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.4);z-index:999999}div._1bm7ugs *{box-sizing:border-box}div._1bm7ugs div.ittm5a{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);display:flex;flex-direction:column;justify-content:flex-start;min-height:25vh;width:50%;background-color:#fff;border:none;border-radius:1em;box-shadow:0 0 10px rgba(0,0,0,0.3);text-align:center;font-size:13px;font-family:Arial,Helvetica,sans-serif;font-weight:bold;line-height:2;color:#000000}div._1bm7ugs div.ittm5a *:before,div._1bm7ugs div.ittm5a *:after{content:"";display:none}@media screen and (max-width:479px){div._1bm7ugs div.ittm5a{font-size:13px;width:90%}}@media screen and (min-width:480px){div._1bm7ugs div.ittm5a{font-size:14px;width:80%}}@media screen and (min-width:608px){div._1bm7ugs div.ittm5a{font-size:14px;width:70%}}@media screen and (min-width:960px){div._1bm7ugs div.ittm5a{font-size:16px;width:70%}}@media screen and (min-width:1200px){div._1bm7ugs div.ittm5a{font-size:16px;width:840px}}div._1bm7ugs div.ittm5a div._1wmm6xd{width:100%;background-color:transparent;border:0;color:inherit;display:block;font-size:1em;font-family:inherit;letter-spacing:normal;margin:0;opacity:1;outline:none;padding:1em 2em;position:static;text-align:center}div._1bm7ugs div.ittm5a div._1wmm6xd img{display:inline;margin:0 0 16px 0;padding:0;max-width:240px;max-height:60px}div._1bm7ugs div.ittm5a div._1wmm6xd h2{display:block;line-height:1.3;padding:0;font-family:inherit;font-weight:normal;font-style:normal;text-decoration:initial;text-align:center;font-size:1.75em;margin:0;color:inherit}div._1bm7ugs div.ittm5a div._1wmm6xd h2:not(img+*){margin-top:30px}div._1bm7ugs div.ittm5a div._1wmm6xd span._10cqbyd{position:absolute;top:0;right:15px;font-size:2em;font-weight:normal;cursor:pointer;color:inherit}div._1bm7ugs div.ittm5a div._1wmm6xd span._10cqbyd:hover{filter:brightness(115%)}div._1bm7ugs div.ittm5a section{width:100%;margin:0;padding:1em 2em;text-align:center;font-family:inherit;color:inherit;background:transparent}div._1bm7ugs div.ittm5a section p{display:block;margin:0 0 1em 0;line-height:1.5;text-align:center;font-size:1em;font-family:inherit;color:inherit;overflow-wrap:break-word;font-weight:normal;font-style:normal;text-decoration:initial}div._1bm7ugs div.ittm5a section p:last-of-type{margin:0 0 1.5em 0}div._1bm7ugs div.ittm5a section.zgt4cb{display:block}div._1bm7ugs div.ittm5a section.zgt4cb._15yer7c{display:none}div._1bm7ugs div.ittm5a section.zgt4cb a.mhlzqc._1emtbbe{color:var(--_1emtbbe)}div._1bm7ugs div.ittm5a section.zgt4cb a.mhlzqc.g0b7gl{text-decoration:var(--g0b7gl)}div._1bm7ugs div.ittm5a section.zgt4cb a.mhlzqc._1h4vo4h:visited{color:var(--_1h4vo4h)}div._1bm7ugs div.ittm5a section.zgt4cb div.axqbiy{display:block;margin:0.75em;padding:0}div._1bm7ugs div.ittm5a section.zgt4cb div.axqbiy p._10lak7c{max-width:80%;margin:0 auto;padding:0;font-size:0.85em;color:inherit;font-style:normal;font-weight:normal;cursor:pointer}div._1bm7ugs div.ittm5a section.g3f52f{display:block}div._1bm7ugs div.ittm5a section.g3f52f._15yer7c{display:none}div._1bm7ugs div.ittm5a section.g3f52f h4._1e70kqd{color:inherit;text-align:initial;font-weight:normal;font-family:inherit;font-size:1.125em;margin:0 0 0.5em 0.5em}div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss{display:flex;margin:1.5em 0}div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss ul._19i18vc{max-height:300px;flex:2;list-style:none;overflow-y:auto;margin:0 1em 0 0;padding-inline-start:0}@media screen and (min-width:608px){div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss ul._19i18vc{flex:1;margin:0 2em 0 0}}div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss ul._19i18vc li{padding:0.75em;cursor:pointer;background:rgba(0,0,0,0.05);font-weight:bold}div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss ul._19i18vc li:hover{background:rgba(0,0,0,0.075)}div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss ul._19i18vc li._1lowx25{color:var(--_1lsu7q);background:var(--vot3fy)}div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss div._1kebabo{max-height:300px;overflow-y:auto;flex:3;display:flex;flex-direction:column;justify-content:space-between;text-align:initial}div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss div._1kebabo ol._130j852{display:none;list-style-type:decimal;text-align:initial;padding:0;margin:0 2em;font-weight:normal}div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss div._1kebabo ol._130j852._1lowx25{display:block}div._1bm7ugs div.ittm5a section.g3f52f div.i5jiss div._1kebabo p{margin:1em 0 0;text-align:inherit;font-style:italic}div._1bm7ugs div.ittm5a section.g3f52f button.hrbmcw{font-size:1em;text-transform:initial}div._1bm7ugs div.ittm5a button._10jcka8{width:auto;height:auto;max-width:90%;cursor:pointer;display:inline-block;letter-spacing:normal;margin:0.75em;opacity:1;outline:none;overflow-wrap:break-word;font-family:inherit;font-weight:normal;font-style:normal;text-decoration:initial;text-transform:uppercase;text-align:center;color:#FFFFFF;font-size:1.15em;padding:0.75em 2em;padding-inline:2em;padding-block:0.75em;line-height:normal;background:#40C28A;border:none;border-radius:0.25em;box-shadow:none}div._1bm7ugs div.ittm5a button._10jcka8:hover{filter:brightness(115%);box-shadow:none}div._1bm7ugs div.ittm5a a._1ahf6lp{height:50px;width:50px;position:absolute;bottom:5px;right:5px}div._1bm7ugs div.ittm5a a._1ahf6lp img{position:initial;height:100%;width:100%;filter:drop-shadow(1px 1px 1px var(--_1n05zg8))}')),document.head.appendChild(A));var A;var at="aHR0cHM6Ly9hLnB1Yi5uZXR3b3JrL2NvcmUvcHJlYmlkLXVuaXZlcnNhbC1jcmVhdGl2ZS5qcw==",rt="aHR0cHM6Ly93d3cuZ29vZ2xldGFnc2VydmljZXMuY29tL3RhZy9qcy9ncHQuanM=",dt="aHR0cHM6Ly9hLnB1Yi5uZXR3b3JrL2NvcmUvaW1ncy8xLnBuZw==",lt="ZGF0YS1mcmVlc3Rhci1hZA==",ct="c2l0ZS1jb25maWcuY29t";var mt="aHR0cHM6Ly9mcmVlc3Rhci5jb20vYWQtcHJvZHVjdHMvZGVza3RvcC1tb2JpbGUvZnJlZXN0YXItcmVjb3ZlcmVk",ut=["Y29uZmlnLmNvbmZpZy1mYWN0b3J5LmNvbQ==","Y29uZmlnLmNvbnRlbnQtc2V0dGluZ3MuY29t","Y29uZmlnLnNpdGUtY29uZmlnLmNvbQ==","Y29uZmlnLmZyZmlndXJlcy5jb20="];var y="ZnMtYWRiLWVycg",gt=()=>h(void 0,null,function*(){document.body||(yield new Promise(n=>document.addEventListener("DOMContentLoaded",n)));let o=["YWQ=","YmFubmVyLWFk","YmFubmVyX2Fk","YmFubmVyLWFkLWNvbnRhaW5lcg==","YWQtc2lkZXJhaWw=","c3RpY2t5YWRz","aW1wcnRudC1jbnQ="],t=document.createElement("div");t.textContent=Math.random().toString(),t.setAttribute(atob(lt),Math.random().toString());for(let n=0;n<o.length;n++)t.classList.add(atob(o[n]));t.style.display="block",document.body.appendChild(t);let i=window.getComputedStyle(t),e=i==null?void 0:i.display;if(t.remove(),e==="none")throw new Error(y)}),H=(o,t=!1)=>h(void 0,null,function*(){return new Promise((i,e)=>{let n=document.createElement("script");try{n.src=o,n.addEventListener("load",()=>{t?ft(o,i,e):i()}),n.addEventListener("error",()=>{e(y)}),document.head.appendChild(n)}catch(s){e(s)}finally{n.remove()}})}),pt=(...t)=>h(void 0,[...t],function*(o=atob(dt)){return new Promise((i,e)=>{let n=encodeURIComponent(new Date().toISOString().split("Z")[0]),s=document.createElement("img");s.src=\`\${o}?x=\${n}\`,s.onload=()=>h(void 0,null,function*(){yield ft(o,i,e),i(),s.remove()}),s.onerror=()=>{e(y),s.remove()},document.body.appendChild(s)})}),ft=(o,t,i)=>h(void 0,null,function*(){try{let e=yield fetch(o),n=e==null?void 0:e.redirected,s=e==null?void 0:e.url;n||(s?s!==o:!1)?i(y):t()}catch(e){i(y)}});var ht="data:image/png;base64,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";var bt="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEsAAABLCAMAAAAPkIrYAAAC8VBMVEUAAAAAwoj+/v7+/v4Awoj+/v4AwYj+/v4Ss4K3t7f9/f38/Pz9/f0IuYT+/v78/Pz+/v79/f39/f39/f0IVT329vb8/Pz8/PwFnoP19fX19fX8/Pz9/f39/f36+vrz8/P19fX19fX39/f7+/sStIP29vYH1b0AwYf8/Pz19fX4+Pj4+Pj09PTx8fEAwoj6+vr6+vr5+fn39/f29vb29va4uLi4uLj9/f37+/sHuoT7+/v5+fn6+vr19fX6+vr39/f09PT6+vr09PS4uLi4uLj09PS4uLj9/f0BwYf9/f38/Pz7+/v29vb4+Pj19fX09PT6+vry8vL4+Pj19fX4+Pjz8/P29vb29vb4+Pjo6Oj4+Pj4+Pjd3d0IuX74+Pjz8/MDiWP8/Pz8/Pz6+vr29vb7+/v39/f4+Pj6+vrv7+/29vb19fXm5ubu7u739/cEXUL6+vr9/f36+vr5+fkHsIT6+vrw8PD09PT6+vr39/cBwIf9/f0BwIf6+voIuYQEvYUGuoQEvIX6+voHuIP39/fu7u7h4eEHsYLt7e37+/v19fXy8vLv7+/x8fHx8fEGvYrBwcHx8fEFr5UBwIcBwYcDvoYHu4UDvob39/cGuoP6+voJtIUJtIMKrYGEXULt7e3y8vLo6OgIVT0IlX3W1tYHtI8Kv2IehWYhgGQDv4cBwIcEv4YFvIYEvob+/v4KtIAFu4T39/cItoELqoAHuoUGvoPn5+fv7+8NrH7JyckIuXwGs4UDrpMItYTHx8f09PTk5OQGyXUJrJIEmYB8fHwHv5V8fHwJroUGxoHi4uIOoHgExIQIVT4Fsoi7u7v5+fkJsnL09PQGmHIGkXkHtGsRoXcJtIAUoXYIrHzg4ODw8PDp6ekFvYbu7u7Dw8MGxYMEnWIDnXIJx3sMpn7Ozs4Dwaa2trYGyYPX19e7u7sUoIQIVD0H14sH2H8Kums/qEe8vLwgpmLh4eEAw4kAwogQonYdh2cDvYUNqXoZkW0ggGSqNDsXAAAA83RSTlMA/vz+/Pf1+qkH8eX1zfjT++rv7i8I4dZiLR4L8+bGaCsVE8unki732I82LyUO+r64ops0FwkF487LyLWzrn9YVT08FA8KBPTv7N3DtaihmIyEfXt2dW5gW1VLQiwZEAYE3trBsKyll5SPiIaGe29tY11STkdGOighGfPo4dLOvLaxsI6Kh399cWtrZFxSQR4cEhLs6N7Jxr2tnJSDdW9kYE0wLiclEvb07OPWzsO0pqKdmoyFfXZzbm1sYlhNTElIRz8/Pj09Ojk1NTAvLCsmJBwaDQn14Mq0qqejo4t6c2xpaGBcWVhXPDsxMCsqKiIZDw1727kAAAAH5klEQVRYw9WXVYDTQBCG/8w2lAoVChQ93N3d3d3d3d3d3d3d3d3d3d2dFn1iQkvpNSkU3vjuIbud5L+d2dnJBP8hmfdVTJzT6YwJ5KzfMEOeUePxbxRJ2SqhmYhkWRwCIutj89iSLHlm/DWZhjXgR8OajYhRpLk4wloNEXNUuVyyyIi/JGaG2CQ3qpYJCh6tkmBSG61ASnup4wiV+IllilYhvmeSOmXuHz5aU7ByXGoJJBdka9QVIVE1DmUdCYUPcTNkswhBz4HifCmRvrclJgxxHPMTkr58KIEqRbHzxOJBoWppOfSObKVbPeJZ5zvrS8YhCuMRlUGRvCaypsQfKGCisFR8TdDaQSJHywLhjKm2nAHKUF4exmosouH3tJP1efgStYKdHC1iQIsBwlImEwpbqQ1+Sx5y1OBLfifZ8xQOlnfVrFQ0bjpqyuMOwbOtPJmUtQzSU7P4CE6WKrFJTIPibtpgYhWpfjcOflqqX+BP+ZcuYQKgEskid5BcECaOeq0GIldqhEIH6hmjJLuqQRezsRYQz0StiiAUUhnlFBhvFXHVpoIl9B2BRXFEJYRGvMhUgxUj69UHqjSVY8FkFKoU77YsdwHmkzNqgCEfJTSgSCnRAri0+HWShfyXJArzcsGCBVF+cuoU/6rwavElADUspvOGKrKojnCkjmbhbKhAObNg23IpBIoN/bHxYVYyV0Z4WlNyIIUcrSBm6yRXCEjSQHC1JUv67oFVRu+IBUMOWw0sSSq5JD9Y2C1pc45zsVlG5cSNrOWnNVg5XHHpJnCQpVYmYiJ6WO5y6ZSZPzxfxbft+hnqEpQWPqLa7YUQ1VQ0pkdr8gn4yO5yRYCatZJL59GKMY30Jkrhs8SlxEBlJWR434PFIuzwmSK5XEmh4nQaSUpTG0wCvUgXbw+V9ZnScrnMkiz2j/P8MA2L6aZP+K1Wf75ng+ecO23dYLBH/llVuokwThZK75k9LcY3+vzMrqV1VOeSetT2VjwaDLSk6j4Xqyj142dxeDtVYrWks+sF1erH9o3ecSFjMmAMNfFOc4quiGqMkwVe6g1UxHT9LwSJ/QFOiAi14aUxFUhZTf5ZsIubFOVBfnd3KiYxvQ4DUzS0VktuaZbfxpEg8gasG2UAklM++DF2qk7xc7OWViedW5o4wTet48xWekhnbx2eq9SGdPqAqrxJEdP1nazWmsq5tRnaJKfRQNYSCGD/JMnNG+oO1NqtLOsi/DG0L+gdpaeznHJpEUjtvrwFTNKd0f3YuZKXtSO81HXqHdMzTCdHRUbOLhX1hv6oGVIALqlXuGUVChPFqb7neOdwAPtEYmjQaQX7GIik2wY/uicUuaKWp+JdwPSOA1QXg6FF7Stut+d5P9bBj64NRBMDMFwY9/Is2w+tQdDk6scfYtKse20V5syZc/+wvz1jT2oFhWpmuRqQsDiwRySHJtk/ff2s7OekTprmFEZRdM0ALu9P1tQXVBVpYwP5qWkQrS/fll77caQ21oOauLJe1tN2dk5YZHkGH6gE6EqNoUkk6dMyDP1Rh1ZfgJpYsWINt7UHTPo6hWMBMyg/CsvWIFruj8uAY70kF/u5G1oM4eejxo7m7W5GAA2KGoJouZfyZUl/5Uil2aTlZy4xjnO9kbfHaME/UAptLZf7MhTuKn5Ks6EmTuy6HO+KYFgznbK4ucG0eoDx+CmtgIrutkaKn/O8wkYDUlBiaNHnV504x5W5GFRUocrAAIrnbUtEStR1OKJqr8unNZTX1VcjXJQS6Pmzro5SnC1LnX+vNaGH5NYdUPdaZitQ01cautOPaaPf+zhQe1nbqTzQgny9Tik9e2s1j/udVu1ivKyjKrshmqU7MheP7AvQPMqgxLDs77QGcuT7qe2jlEer+29cViNX+2hyquDxGstvfd1Jtb2BjV0Ko4z+7VcFoCrlDq61gaO1Vm1uTWWUUKfzbwuNRRMgS0PKF0xrcQRuRt6orGfNkdmhkjQmfMKx2ynNpnFBtG7xstapP2fCqKrS6uaEP3WT2djlypTToKl1mt/USceqtBJTKSCWScRDODpT78xAbpFYU2s6L2umxodKiQTAemoZaMhAzTiJnZRHQ+uFjvs71bI66o1cnEeTs26gpZBV+caMmVUoYgE9Zj/JrV5WDbP8DOhqtNSCilpG5TMiVTQq6x8zpTfppBSICQjPCIt5NBDfKdppfncJ+yJgkVOkix++bZP4rT8r4OiUFY4CnEslOTKajLCZznDMSpFpr++3B542YOIS+BMjm3Dy5sVqSKURhGHkYDdRwULpz8PLdJ3SBe4PF9pWNsodC3gXJqYZEIzhtthK6qdISJFvj/c2KI/7r5rpfxCjVjaRXbkrRjK6YUBw8hqpHNsNbeKQuWwBqIk3xE6xk6dWiktsKoffwv8sW0pwKNqYSCQrNz9c4R7TMocgcwvloCUoLeS8+AN10pPcvA4PMnccEJnInCxX4i3D27Xb2ryx1Ugkh41UfDfEtVPCePgzHaORo02sH6d0dOKGRiHIZrOREJaETePW8UTCSnJFhEShCmayN/d2jVm65e/Yvn37DjXjFfYufKuV9E3YGiIJkttJHzZSXWcz5W1iJjl3DPwNqedymIVzRuuaqeEhc4phTXPoiUyVY+KvSVUprYM4WJF7Zs0aLY6RZahowsEZs+AfyThsUJOSJexGe9YcuctVqlkX/yffAdbeMQWIuBUAAAAAAElFTkSuQmCC";var F=class{constructor(t){this.config=null,this.langCode=null,this.languages=this.getUserPreferredLanguages(t)}init(){return h(this,null,function*(){this.config=yield this.fetchConfig(),this.config!==null&&(this.langCode=this.getFirstSupportedLanguage(this.languages),this.observe())})}fetchConfig(){return h(this,null,function*(){let t=ut,i=t.length-1,e=Number.isNaN(Number(localStorage.getItem("fs.cdi")))?0:Number(localStorage.getItem("fs.cdi")),n=Number.isNaN(Number(localStorage.getItem("fs.cfc")))?0:Number(localStorage.getItem("fs.cfc")),d=\`https://\${atob(t[e])}/fortune-com.json\`;try{return(yield fetch(d)).json()}catch(l){return n++,n>=3&&(n=0,e++),e>i&&(e=0),null}finally{localStorage.setItem("fs.cdi",e),localStorage.setItem("fs.cfc",n)}})}killScroll(t){if(t.isScrollDisabled){this.existingOverflow=document.body.style.overflow,document.body.style.overflow="hidden";let i=window.pageYOffset||document.documentElement.scrollTop,e=window.pageXOffset||document.documentElement.scrollLeft;document.body.style.top=\`-\${i}px\`,document.body.style.left=\`-\${e}px\`,window.onscroll=function(){window.scrollTo(e,i)}}}reviveScroll(){document.body.style.overflow=this.existingOverflow||"",window.onscroll=function(){}}getUserPreferredLanguages({languages:t,language:i}){let e=t===void 0?[i]:t;if(e)return e.map(n=>{let s=n.trim().toLowerCase();if(!s.includes("zh"))return s.split(/-|_/)[0];let d=s.split(/-|_/)[1],l=["hans","cn","sg"],u=["hant","hk","mo","tw"];if(s==="zh"||l.includes(d))return"zh";if(u.includes(d))return"zh-hant"})}getFirstSupportedLanguage(t){let i=["title","paragraphOne","buttonText"],e=t.find(n=>i.every(s=>!!this.config[s][n]));return e!==void 0?e:"en"}getLocalizedTextContent(t,i,e=!1){var s;let n=t[i];if(n===void 0)throw new Error(\`Config text not found for text key \${i}\`);return e?(s=n[this.langCode])!=null?s:n.en:n[this.langCode]}getPixelString(t){return typeof t=="number"?\`\${t}px\`:null}pickContrastingColorValue(t,i,e){let n=t.substring(1,7),s=parseInt(n.substring(0,2),16),d=parseInt(n.substring(2,4),16),l=parseInt(n.substring(4,6),16);return s*.299+d*.587+l*.114>=128?i:e}generateOverlay(t){let{siteId:i,isCloseEnabled:e,dismissDuration:n,dismissDurationPv:s,logoUrl:d,font:l,paragraphTwo:u,paragraphThree:v,closeText:E,linkText:U,linkUrl:O,textColor:T,headerTextColor:S,buttonTextColor:w,headerBgColor:X,bgColor:I,buttonBgColor:L,borderColor:V,borderWidth:vt,borderRadius:xt,closeButtonColor:\$,closeTextColor:Q,linkTextColor:Z,linkTextDecoration:j,linkVisitedTextColor:J,hasFsBranding:yt,disableInstructions:_t}=t,g=document.createElement("div");g.style.setProperty("--vot3fy",L||"#40C28A"),g.style.setProperty("--_1lsu7q",w||"#000000"),g.style.setProperty("--_1n05zg8",this.pickContrastingColorValue(I||"#FFFFFF","white","black")),Z&&g.style.setProperty("--_1emtbbe",Z),J&&g.style.setProperty("--_1h4vo4h",J),j&&g.style.setProperty("--g0b7gl",j),g.classList.add("_1bm7ugs"),g.id="ndeb5g",g.dir="auto",this.oid=g.id;let p=document.createElement("div");p.classList.add("ittm5a"),I&&(p.style.backgroundColor=I),l&&(p.style.fontFamily=l),T&&(p.style.color=T);let Y=this.getPixelString(xt),q=this.getPixelString(vt);Y&&(p.style.borderRadius=Y),(V||q)&&(p.style.borderStyle="solid"),V&&(p.style.borderColor=V),q&&(p.style.borderWidth=q);let b=document.createElement("div");if(b.classList.add("_1wmm6xd"),S&&(b.style.color=S),X){b.style.backgroundColor=X;let a=Y||"1em";b.style.borderTopLeftRadius=a,b.style.borderTopRightRadius=a}if(d){let a=document.createElement("img");a.src=d,a.alt="Logo",a.onerror=function(){this.style.display="none"},b.appendChild(a)}let K=document.createElement("h2");K.textContent=this.getLocalizedTextContent(t,"title"),b.appendChild(K);let x=document.createElement("section");x.classList.add("zgt4cb");let tt=document.createElement("p");if(tt.textContent=this.getLocalizedTextContent(t,"paragraphOne"),x.appendChild(tt),u&&Object.keys(u).length!==0){let a=document.createElement("p");a.textContent=this.getLocalizedTextContent(t,"paragraphTwo"),x.appendChild(a)}if(v&&Object.keys(v).length!==0){let a=document.createElement("p");a.textContent=this.getLocalizedTextContent(t,"paragraphThree"),x.appendChild(a)}let et=U&&this.getLocalizedTextContent(t,"linkText"),it=O&&this.getLocalizedTextContent(t,"linkUrl",!0);if(et&&it){let a=document.createElement("div");a.style.margin="0 0 1em";let r=document.createElement("a");r.classList.add("mhlzqc"),Z&&r.classList.add("_1emtbbe"),J&&r.classList.add("_1h4vo4h"),j&&r.classList.add("g0b7gl"),r.textContent=et,r.href=it,r.target="_blank",a.appendChild(r),x.appendChild(a)}let _=document.createElement("button");if(_.classList.add("_10jcka8"),_.tabIndex=0,_.textContent=this.getLocalizedTextContent(t,"buttonText"),L&&(_.style.backgroundColor=L),w&&(_.style.color=w),_.onclick=function(){document.querySelector("section.zgt4cb").classList.add("_15yer7c"),document.querySelector("section.g3f52f").classList.remove("_15yer7c")},x.appendChild(_),e){let a=()=>{if(g.remove(),this.reviveScroll(),!n&&!s){sessionStorage.setItem(\`fs.adb\${i||""}.dis\`,"1");return}sessionStorage.removeItem(\`fs.adb\${i||""}.dis\`),s?this.updateValues("p"):n&&this.updateValues("dt")},r=document.createElement("span");if(r.classList.add("_10cqbyd"),r.innerHTML="&times;",r.tabIndex=0,\$&&(r.style.color=\$),r.addEventListener("click",a),b.appendChild(r),E&&Object.keys(E).length!==0){let f=document.createElement("div");f.classList.add("axqbiy");let m=document.createElement("p");m.classList.add("_10lak7c"),m.textContent=this.getLocalizedTextContent(t,"closeText"),Q&&(m.style.color=Q),m.addEventListener("click",a),f.appendChild(m),x.appendChild(f)}}let Ct=a=>{let r=document.querySelectorAll("._19i18vc > li"),f=document.getElementsByClassName("_130j852");for(let m=0;m<f.length;m++)r[m].classList.remove("_1lowx25"),f[m].classList.remove("_1lowx25");r[a].classList.add("_1lowx25"),f[a].classList.add("_1lowx25")},k=document.createElement("section");k.classList.add("g3f52f","_15yer7c");let M=document.createElement("h4");M.classList.add("_1e70kqd"),M.textContent=this.getLocalizedTextContent(t,"instructionsTitle");let R=document.createElement("div");R.classList.add("i5jiss");let P=document.createElement("ul");P.classList.add("_19i18vc");let z=document.createElement("div");z.classList.add("_1kebabo"),_t.forEach((a,r)=>{let f=document.createElement("li");f.onclick=()=>Ct(r),f.textContent=this.getLocalizedTextContent(a,"name",!0),P.appendChild(f);let m=document.createElement("ol");m.classList.add("_130j852"),r===0&&(f.classList.add("_1lowx25"),m.classList.add("_1lowx25")),this.getLocalizedTextContent(a,"steps").forEach(Lt=>{let ot=document.createElement("li");ot.textContent=Lt,m.appendChild(ot)}),z.appendChild(m)});let wt=this.getLocalizedTextContent(t,"disclaimerText"),nt=document.createElement("p");nt.textContent=wt,z.appendChild(nt),R.appendChild(P),R.appendChild(z);let C=document.createElement("button");if(C.classList.add("_10jcka8","hrbmcw"),C.textContent=this.getLocalizedTextContent(t,"backButtonText"),L&&(C.style.backgroundColor=L),w&&(C.style.color=w),C.onclick=function(){document.querySelector("section.g3f52f").classList.add("_15yer7c"),document.querySelector("section.zgt4cb").classList.remove("_15yer7c")},k.appendChild(M),k.appendChild(R),k.appendChild(C),p.appendChild(b),p.appendChild(x),p.appendChild(k),yt){let a=document.createElement("a");a.classList.add("_1ahf6lp"),a.href=atob(mt),a.target="_blank";let r=document.createElement("img");r.alt="Logo",r.src=this.pickContrastingColorValue(I||"#FFFFFF",ht,bt),a.appendChild(r),p.appendChild(a)}return g.appendChild(p),g}getAndSetOverlay(t){return h(this,null,function*(){if(this.post(!0,t),!t.dismissDuration&&!t.dismissDurationPv&&sessionStorage.getItem(\`fs.adb\${t.siteId||""}.dis\`)==="1")return;let i=localStorage.getItem("fs.adb"),e=i&&JSON.parse(i);if(t.dismissDurationPv&&e.p&&typeof e.p=="number")if(t.dismissDurationPv<=e.p+1)this.clearValue("p");else{this.updateValues("p");return}else this.clearValue("p");let n=parseInt(e.dt,10);if(t.dismissDuration&&n){if(Math.abs((Date.now()-n)/36e5)<t.dismissDuration)return;this.clearValue("dt")}else this.clearValue("dt");if(document.body||(yield new Promise(d=>document.addEventListener("DOMContentLoaded",d))),this.killScroll(t),document.querySelector(\`#\${this.oid}\`)!==null)return;let s=this.generateOverlay(t);document.body.appendChild(s)})}getStatus(t,i){return i===!0?1:t===2||t===1?2:0}getAndSetData(t){let i=localStorage.getItem("fs.adb"),e=i&&JSON.parse(i),n=Date.now(),s,d,l;return e?(s=e.i,d=e.ot,l=this.getStatus(e.s,t)):(e={},s=B(),d=n,l=t?1:0),e.i=s,e.s=l,e.ot=d,e.lt=n,localStorage.setItem("fs.adb",JSON.stringify(e)),e}updateValues(t){let i=localStorage.getItem("fs.adb"),e=i&&JSON.parse(i);t==="p"?(e.p=e.p?e.p+1:1,e.dt&&delete e.dt):t==="dt"&&(e.dt=Date.now(),e.p&&delete e.p),localStorage.setItem("fs.adb",JSON.stringify(e))}clearValue(t){let i=localStorage.getItem("fs.adb"),e=i&&JSON.parse(i);e[t]&&(delete e[t],localStorage.setItem("fs.adb",JSON.stringify(e)))}post(t,i){let e=atob(ct),s=\`https://\${i.cDomain||e}/v2/abr\`,d=this.getAndSetData(t),{accountId:l,siteId:u}=i,v=navigator.userAgent||window.navigator.userAgent,E=document.referrer,U=window.location,O=S=>{switch(S){case 0:return"not detected";case 1:return"detected";case 2:return"recovered";default:return}},T={accountId:l,siteId:u,userId:d.i,url:U.href,referalURL:E,userAgent:v,status:O(d.s),returning:d.ot!==d.lt,version:"1.4.2"};fetch(s,{method:"POST",headers:{"Content-Type":"application/json","X-Client-Geo-Location":"{client_region},{client_region_subdivision},{client_city}"},body:JSON.stringify(T)}).catch(()=>{})}observe(){let t="",i=new MutationObserver(()=>{location.pathname!==t&&(t=location.pathname,this.run())}),e={subtree:!0,childList:!0};i.observe(document,e)}run(){let t=this.config;setTimeout(()=>h(this,null,function*(){try{yield gt(),yield pt(),yield H(atob(at),!0),yield H(atob(rt),!1),this.post(!1,t)}catch(i){(i===y||(i==null?void 0:i.message)===y)&&(yield this.getAndSetOverlay(t))}}),500)}};var Rt=["googlebot","mediapartners-google","adsbot-google","bingbot","slurp","duckduckbot","baiduspider","yandexbot","konqueror/3.5","Exabot/3.0","facebot","facebookexternalhit/1.0","facebookexternalhit/1.1","ia_archiver"],N=class{constructor(t){this.globalNavigator=t}checkForBot(){let t=this.globalNavigator.userAgent;t&&Rt.forEach(i=>{if(RegExp(i.toLowerCase()).test(t.toLowerCase()))throw new Error("bot detected")})}};var zt=new N(window.navigator);zt.checkForBot();var Dt=new F(window.navigator);Dt.init();})();
          `}
        </script>
      )}

      {isBranded && (
        <script
          type='text/javascript'
          src='https://s.ntv.io/serve/load.js'
          data-ntv-article
          async
        ></script>
      )}

      {personalizedAds && (
        <>
          <link
            rel='dns-prefetch'
            href='https://c.amazon-adsystem.com'
          />
          <link
            rel='preconnect'
            href='https://c.amazon-adsystem.com'
            crossOrigin=''
          />
          <script
            async
            src='https://c.amazon-adsystem.com/aax2/apstag.js'
          />
        </>
      )}

      {performanceTracking && (
        <>
          <script
            src='https://cdn.amplitude.com/script/3047587b01b1232e23931a810f3a5279.js'
            async
          />
          <script
            id='amplitude-init'
            async
            dangerouslySetInnerHTML={{
              __html: `window.amplitude.add(window.sessionReplay.plugin({sampleRate: 1}));window.amplitude.init('3047587b01b1232e23931a810f3a5279', {"autocapture":{"elementInteractions":true}});`,
            }}
          />
        </>
      )}
      {performanceTracking && (
        <script
          id='mixpanel-init'
          async
          dangerouslySetInnerHTML={{
            __html: mixPanelSrc,
          }}
        />
      )}
    </NextHead>
  );
};

export default Head;
